import {Box, Link, Text} from '@constellation/core';
import {memo} from 'react';
import {styled} from 'styled-components';
import {type transactionChargesProps} from '../../../../../types/cardPaymentServiceDetails.js';
import {
  CARD_PAYMENTS,
  SCHEME_MASTERCARD,
  SCHEME_VISA,
} from '../../../../../utils/constants/public/psr.js';
import {getPsrLabel} from '../../../../../utils/index.js';
import {
  getQuotationPercent,
  getTotalPercent,
} from '../../../../../utils/quotation.js';
import {
  AuthorisationFeeLabel,
  AuthorisationFeeValue,
  AuthorisationGrid,
  CardFullWidthLine,
  FullWidthLine,
  GridItemHeadingLine,
  SmallLineDown,
  SmallLineUp,
  TableCellEven,
  TableCellOdd,
  TableHeadEven,
  TableHeadOdd,
  TableLabelCell,
  TableModeCell,
  TransactionTable,
} from '../Styles/Styles.js';

const Div = styled(Box)`
  ${(props) => `
  border-left: 1px solid;
  padding: 0px 12px 12px;
  @media (min-width: ${props.theme.breakpoint_lg}) {
    display: none;
  }
  @media (width: ${props.theme.breakpoint_lg}) {
    display: block;
  }
`}
`;

const TransactionChargesSmallScreen = memo((props: transactionChargesProps) => {
  const {schemeRates, authorisationFee} = props;
  const mastercardRates = schemeRates?.find(
    (rate) => rate.schemeName === SCHEME_MASTERCARD
  );
  const visaRates = schemeRates?.find(
    (rate) => rate.schemeName === SCHEME_VISA
  );
  const mdFace = getQuotationPercent(mastercardRates?.debit?.face);
  const mcFace = getQuotationPercent(mastercardRates?.credit?.face);
  const mbFace = getQuotationPercent(mastercardRates?.business?.face);
  const vdFace = getQuotationPercent(visaRates?.debit?.face);
  const vcFace = getQuotationPercent(visaRates?.credit?.face);
  const vbFace = getQuotationPercent(visaRates?.business?.face);
  const mdPhoneOnline = getQuotationPercent(
    mastercardRates?.debit?.online || mastercardRates?.debit?.moto
  );
  const mcPhoneOnline = getQuotationPercent(
    mastercardRates?.credit?.online || mastercardRates?.credit?.moto
  );
  const mbPhoneOnline = getQuotationPercent(
    mastercardRates?.business?.online || mastercardRates?.business?.moto
  );
  const vdPhoneOnline = getQuotationPercent(
    visaRates?.debit?.online || visaRates?.debit?.moto
  );
  const vcPhoneOnline = getQuotationPercent(
    visaRates?.credit?.online || visaRates?.credit?.moto
  );
  const vbPhoneOnline = getQuotationPercent(
    visaRates?.business?.online || visaRates?.business?.moto
  );

  return (
    <Div bgColor='neutral2' marginBottom='none'>
      <SmallLineUp />
      <Box bgColor='neutral2' marginBottom='03' padding='none'>
        <TransactionTable aria-describedby={CARD_PAYMENTS}>
          <col />
          <colgroup span={3} />
          <tr>
            <th />
            <th colSpan={3} scope='colgroup'>
              <Text weight='bold'>
                {getPsrLabel(
                  'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardTypes.type1'
                )}
              </Text>
              <GridItemHeadingLine
                aria-hidden={true}
                marginBottom='none'
                marginTop='01'
              />
            </th>
          </tr>
          <tr>
            <td scope='row'>
              <Text size='s1' weight='bold'>
                {getPsrLabel(
                  'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.labelText'
                )}
              </Text>
              <CardFullWidthLine
                aria-hidden={true}
                marginBottom='none'
                marginTop='none'
              />
            </td>
            <TableHeadOdd scope='col'>
              <Text size='s1'>
                {getPsrLabel(
                  'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category1'
                )}
                <small>
                  <sup>
                    <Link
                      aria-label={`footnote ${getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category1Ref'
                      )}`}
                      href='#personaldebitcard'
                      id='personaldebitcard-ref-small'
                    >
                      {getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category1Ref'
                      )}
                    </Link>
                  </sup>
                </small>
              </Text>
              <FullWidthLine
                aria-hidden={true}
                marginBottom='none'
                marginTop='01'
              />
            </TableHeadOdd>
            <TableHeadEven scope='col'>
              <Text size='s1'>
                {getPsrLabel(
                  'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category2'
                )}
                <small>
                  <sup>
                    <Link
                      aria-label={`footnote ${getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category2Ref'
                      )}`}
                      href='#personalcreditcard'
                      id='personalcreditcard-ref-small'
                    >
                      {getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category2Ref'
                      )}
                    </Link>
                  </sup>
                </small>
              </Text>
              <FullWidthLine
                aria-hidden={true}
                marginBottom='none'
                marginTop='01'
              />
            </TableHeadEven>
            <TableHeadOdd scope='col'>
              <Text size='s1'>
                {getPsrLabel(
                  'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category3'
                )}
                <small>
                  <sup>
                    <Link
                      aria-label={`footnote ${getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category3Ref'
                      )}`}
                      href='#businessdebitcard'
                      id='businessdebitcard-ref-small'
                    >
                      {getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category3Ref'
                      )}
                    </Link>
                  </sup>
                </small>
              </Text>
              <FullWidthLine
                aria-hidden={true}
                marginBottom='none'
                marginTop='01'
              />
            </TableHeadOdd>
          </tr>
          <tr>
            <TableModeCell scope='row'>
              <Text size='s1'>
                {getPsrLabel(
                  'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.mode.mode1'
                )}
                <small>
                  <sup>
                    <Link
                      aria-label={`footnote ${getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.mode.mode1Ref'
                      )}`}
                      href='#sameplace'
                      id='sameplace-ref-small'
                    >
                      {getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.mode.mode1Ref'
                      )}
                    </Link>
                  </sup>
                </small>
              </Text>
              <FullWidthLine
                aria-hidden={true}
                marginBottom='none'
                marginTop='01'
              />
            </TableModeCell>
            <TableCellOdd>
              <Text size='s1'>{mdFace}</Text>
              <FullWidthLine
                aria-hidden={true}
                marginBottom='none'
                marginTop='01'
              />
            </TableCellOdd>
            <TableCellEven>
              <Text size='s1'>{mcFace}</Text>
              <FullWidthLine
                aria-hidden={true}
                marginBottom='none'
                marginTop='01'
              />
            </TableCellEven>
            <TableCellOdd>
              <Text size='s1'>{mbFace}</Text>
              <FullWidthLine
                aria-hidden={true}
                marginBottom='none'
                marginTop='01'
              />
            </TableCellOdd>
          </tr>
          <tr>
            <td scope='row'>
              <Text size='s1'>
                {getPsrLabel(
                  'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.mode.mode2'
                )}
                <small>
                  <sup>
                    <Link
                      aria-label={`footnote ${getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.mode.mode2Ref'
                      )}`}
                      href='#notsameplace'
                      id='notsameplace-mon-ref'
                    >
                      {getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.mode.mode2Ref'
                      )}
                    </Link>
                  </sup>
                </small>
              </Text>
            </td>
            <TableCellOdd>
              <Text size='s1'>{mdPhoneOnline}</Text>
            </TableCellOdd>
            <TableCellEven>
              <Text size='s1'>{mcPhoneOnline}</Text>
            </TableCellEven>
            <TableCellOdd>
              <Text size='s1'>{mbPhoneOnline}</Text>
            </TableCellOdd>
          </tr>
        </TransactionTable>
      </Box>
      <Box bgColor='neutral2' marginBottom='03' padding='none'>
        <TransactionTable>
          <col />
          <colgroup span={3} />
          <tr>
            <th />
            <th colSpan={3} scope='colgroup'>
              <Text weight='bold'>
                {getPsrLabel(
                  'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardTypes.type2'
                )}
              </Text>
              <GridItemHeadingLine
                aria-hidden={true}
                marginBottom='none'
                marginTop='01'
              />
            </th>
          </tr>
          <tr>
            <TableLabelCell scope='row'>
              <Text size='s1' weight='bold'>
                {getPsrLabel(
                  'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.labelText'
                )}
              </Text>
              <CardFullWidthLine
                aria-hidden={true}
                marginBottom='none'
                marginTop='none'
              />
            </TableLabelCell>
            <TableHeadOdd scope='col'>
              <Text size='s1'>
                {getPsrLabel(
                  'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category1'
                )}
                <sup aria-hidden={true}>
                  {getPsrLabel(
                    'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category1Ref'
                  )}
                </sup>
              </Text>
              <FullWidthLine
                aria-hidden={true}
                marginBottom='none'
                marginTop='01'
              />
            </TableHeadOdd>
            <TableHeadEven scope='col'>
              <Text size='s1'>
                {getPsrLabel(
                  'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category2'
                )}
                <sup aria-hidden={true}>
                  {getPsrLabel(
                    'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category2Ref'
                  )}
                </sup>
              </Text>
              <FullWidthLine
                aria-hidden={true}
                marginBottom='none'
                marginTop='01'
              />
            </TableHeadEven>
            <TableHeadOdd scope='col'>
              <Text size='s1'>
                {getPsrLabel(
                  'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category3'
                )}
                <sup aria-hidden={true}>
                  {getPsrLabel(
                    'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category3Ref'
                  )}
                </sup>
              </Text>
              <FullWidthLine
                aria-hidden={true}
                marginBottom='none'
                marginTop='01'
              />
            </TableHeadOdd>
          </tr>
          <tr>
            <TableModeCell scope='row'>
              <Text size='s1'>
                {getPsrLabel(
                  'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.mode.mode1'
                )}
                <sup aria-hidden={true}>
                  {getPsrLabel(
                    'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.mode.mode1Ref'
                  )}
                </sup>
              </Text>
              <FullWidthLine
                aria-hidden={true}
                marginBottom='none'
                marginTop='01'
              />
            </TableModeCell>
            <TableCellOdd>
              <Text size='s1'>{vdFace}</Text>
              <FullWidthLine
                aria-hidden={true}
                marginBottom='none'
                marginTop='01'
              />
            </TableCellOdd>
            <TableCellEven>
              <Text size='s1'>{vcFace}</Text>
              <FullWidthLine
                aria-hidden={true}
                marginBottom='none'
                marginTop='01'
              />
            </TableCellEven>
            <TableCellOdd>
              <Text size='s1'>{vbFace}</Text>
              <FullWidthLine
                aria-hidden={true}
                marginBottom='none'
                marginTop='01'
              />
            </TableCellOdd>
          </tr>
          <tr>
            <td scope='row'>
              <Text size='s1'>
                {getPsrLabel(
                  'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.mode.mode2'
                )}
                <sup aria-hidden={true}>
                  {getPsrLabel(
                    'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.mode.mode2Ref'
                  )}
                </sup>
              </Text>
            </td>
            <TableCellOdd>
              <Text size='s1'>{vdPhoneOnline}</Text>
            </TableCellOdd>
            <TableCellEven>
              <Text size='s1'>{vcPhoneOnline}</Text>
            </TableCellEven>
            <TableCellOdd>
              <Text size='s1'>{vbPhoneOnline}</Text>
            </TableCellOdd>
          </tr>
        </TransactionTable>
      </Box>
      <AuthorisationGrid>
        <Text marginBottom='03' size='s3' weight='bold'>
          {getPsrLabel(
            'cardPaymentServicesDetails.transactionCharges.additionalCharges.labelText'
          )}
        </Text>
        <AuthorisationFeeLabel>
          {getPsrLabel(
            'cardPaymentServicesDetails.transactionCharges.additionalCharges.feeLabel'
          )}
        </AuthorisationFeeLabel>
        <AuthorisationFeeValue>
          {getTotalPercent(authorisationFee?.value)}
        </AuthorisationFeeValue>
      </AuthorisationGrid>
      <SmallLineDown />
    </Div>
  );
});

export default TransactionChargesSmallScreen;
